import React, { useState, useEffect } from "react";
import { Zap, Sun, Battery, ArrowLeft, ArrowRight } from "lucide-react";
import "../styles/ProductCarousel.css";

const ProductCarousel = ({ products, selectedProduct, onProductChange }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const getCapacityLabel = (puissance) => {
    if (puissance >= 15000) return "Grandes consommations";
    if (puissance >= 6000) return "Moyennes consommations";
    return "Petites consommations";
  };

  const getCapacityColor = (puissance) => {
    if (puissance >= 15000)
      return "linear-gradient(135deg, #FF6B6B, #FF8E53)";
    if (puissance >= 6000)
      return "linear-gradient(135deg, #4ECDC4, #556270)";
    return "linear-gradient(135deg, #56AB2F, #A8E6CF)";
  };
  
  useEffect(() => {
    const index = products.findIndex(p => p.id === selectedProduct.id);
    if (index !== -1 && index !== currentSlide) {
      setCurrentSlide(index);
    }
  }, [selectedProduct]);

  const handleNextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % products.length);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handlePrevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleSlideSelect = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const currentProduct = products[currentSlide];

  return (
    <div className="product-carousel">
      <div className="carousel-container">
        <div className="text-center mb-5">
          <h2 className="display-5 fw-bold text-primary mb-3">
            Nos Solutions Solaires
          </h2>
          <p className="lead text-muted">
            Choisissez le pack adapté à vos besoins énergétiques
          </p>
        </div>

        {/* Main content */}
        <div className="carousel-content">
          <div className="content-left">
            <div
              className="category-tag"
              style={{ background: getCapacityColor(currentProduct.puissance) }}
            >
              {getCapacityLabel(currentProduct.puissance)}
            </div>

            <h1 className="main-title">
              {currentProduct.name}
              <div 
                className="title-underline"
                style={{ background: getCapacityColor(currentProduct.puissance) }}
              ></div>
            </h1>

            {/* Description */}
            <div className="description-box">
              <p className="main-description">{currentProduct.description}</p>
            </div>

            <button
              className={`cta-button ${
                selectedProduct?.id === currentProduct.id ? "active" : ""
              }`}
              onClick={() => onProductChange(currentProduct)}
            >
              {selectedProduct?.id === currentProduct.id
                ? "✔ Sélectionné"
                : "Choisir ce pack"}
            </button>
          </div>

          {/* Right */}
          <div className="content-right">
            <div className="card-stack">
              {products.map((product, index) => {
                const isActive = index === currentSlide;
                const offset = (index - currentSlide + products.length) % products.length;

                return (
                  <div
                    key={product.id}
                    className={`product-main-card ${isActive ? "active" : ""}`}
                    style={{
                      background: getCapacityColor(product.puissance),
                      transform: `translateX(${offset * 20}px) translateY(${offset *
                        10}px) scale(${isActive ? 1 : 0.9})`,
                      zIndex: products.length - offset,
                      opacity: isActive ? 1 : 0.6,
                    }}
                  >
                    <h3 className="product-type">
                      {product.puissance.toLocaleString()} Wh
                    </h3>
                    <p className="product-description">
                      Pack solaire optimisé pour{" "}
                      {getCapacityLabel(product.puissance).toLowerCase()}.
                    </p>

                    {/* Image */}
                    <div className="product-image">
                      <img
                        src={product.image}
                        alt={product.name}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/300x200/3D5A73/FFFFFF?text=Pack+Solaire";
                        }}
                      />
                    </div>

                    {/* Features */}
                    <div className="product-features">
                      <div className="feature-item">
                        <Zap size={18} className="me-2 text-warning" />
                        <span>
                          Capacité : {product.puissance.toLocaleString()} Wh
                        </span>
                      </div>
                      <div className="feature-item">
                        <Sun size={18} className="me-2 text-info" />
                        <span>Énergie solaire</span>
                      </div>
                      <div className="feature-item">
                        <Battery size={18} className="me-2 text-success" />
                        <span>Batterie incluse</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Control */}
        <div className="carousel-controls">
          <div className="slide-indicators">
            {products.map((product, index) => (
              <button
                key={index}
                className={`indicator ${index === currentSlide ? "active" : ""}`}
                onClick={() => handleSlideSelect(index)}
                style={{
                  background: index === currentSlide 
                    ? getCapacityColor(product.puissance)
                    : "#ccc",
                }}
              />
            ))}
          </div>

          <div className="nav-buttons">
            <button className="nav-btn prev" onClick={handlePrevSlide}>
              <ArrowLeft size={20} />
            </button>
            <button className="nav-btn next" onClick={handleNextSlide}>
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
        <div className="background-elements">
          <div className="bg-wave wave-1"></div>
          <div className="bg-wave wave-2"></div>
          <div className="bg-wave wave-3"></div>
        </div>
      </div>
    </div>
  );
};

export default ProductCarousel;
